import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShoppingCart, LogOut, BadgeCheck, AlertTriangle, Store, MapPin, MessageCircle, ClipboardList, Sparkles, Heart, Clock, Wallet, KeyRound, HelpCircle } from "lucide-react";
import { useSession } from "../context/SessionContext";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ChangePasswordModal } from "./ChangePasswordModal";
import { clientApi, formatCOP } from "../lib/api";
import { openWhatsApp, whatsappUrl } from "../lib/whatsapp";

export const Header = ({ onOpenTour }) => {
  const { customer, logout, usingGenericPwd, mustChangePassword, markPasswordPersonal } = useSession();
  const { count, setOpen } = useCart();
  const navigate = useNavigate();
  const [bodegaStatus, setBodegaStatus] = useState(null);
  const [cartera, setCartera] = useState(null);
  const [pwdOpen, setPwdOpen] = useState(false);

  useEffect(() => {
    if (!customer) return;
    clientApi.get("/portal/delivery-info")
      .then(({ data }) => setBodegaStatus(data?.bodega?.estado || null))
      .catch(() => {});
    clientApi.get("/portal/cartera")
      .then(({ data }) => setCartera(data || null))
      .catch(() => {});
  }, [customer?.nit]);

  // Force-open the modal when the backend flagged must_change_password
  useEffect(() => {
    if (mustChangePassword) setPwdOpen(true);
  }, [mustChangePassword]);

  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <header className="md:sticky md:top-0 z-30 border-b border-cm-border bg-cm-cream/90 backdrop-blur-md" data-testid="header-root">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/catalogo" className="flex items-center gap-2.5 group flex-shrink-0" data-testid="header-logo-link">
            <img src="/cm-logo.jpeg" alt="CM Calidad y Servicio" className="h-10 w-10 rounded-full object-cover ring-2 ring-cm-red/40" />
            <div className="hidden lg:block whitespace-nowrap">
              <div className="font-serif text-base leading-tight tracking-tight">CM Calidad y Servicio</div>
              <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Portal B2B</div>
            </div>
          </Link>

          {/* Customer info se muestra en el sub-header, no aquí — evita apretujarse en pantallas 1024-1280 */}

          <div className="flex items-center gap-2">
            {onOpenTour && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onOpenTour}
                className="text-cm-ink-soft hover:text-cm-ink hover:bg-cm-ink/5"
                title="Ver tutorial de uso"
                data-testid="open-tutorial-btn"
              >
                <HelpCircle className="h-5 w-5" />
              </Button>
            )}
            <Button
              asChild
              variant="ghost"
              className="text-cm-ink-soft hover:text-cm-ink hover:bg-cm-ink/5 hidden md:inline-flex"
              data-testid="my-cartera-link"
            >
              <Link to="/mi-cartera">
                <Wallet className="h-4 w-4" />
                <span className="ml-1.5 hidden lg:inline text-sm">Mi cartera</span>
                {cartera?.status === "rojo" && (
                  <span className="ml-1 inline-flex h-2 w-2 rounded-full bg-red-500" />
                )}
                {cartera?.status === "amarillo" && (
                  <span className="ml-1 inline-flex h-2 w-2 rounded-full bg-amber-400" />
                )}
              </Link>
            </Button>
            <Button
              asChild
              variant="ghost"
              className="text-cm-ink-soft hover:text-cm-ink hover:bg-cm-ink/5 hidden md:inline-flex"
              data-testid="my-suggestions-link"
            >
              <Link to="/sugeridos">
                <Sparkles className="h-4 w-4" />
                <span className="ml-1.5 hidden lg:inline text-sm">Sugeridos</span>
              </Link>
            </Button>
            <Button
              asChild
              variant="ghost"
              className="text-cm-ink-soft hover:text-cm-ink hover:bg-cm-ink/5"
              data-testid="my-favorites-link"
            >
              <Link to="/mis-favoritos">
                <Heart className="h-4 w-4" />
                <span className="ml-1.5 hidden lg:inline text-sm">Favoritos</span>
              </Link>
            </Button>
            <Button
              asChild
              variant="ghost"
              className="text-cm-ink-soft hover:text-cm-ink hover:bg-cm-ink/5"
              data-testid="my-orders-link"
            >
              <Link to="/mis-pedidos">
                <ClipboardList className="h-4 w-4" />
                <span className="ml-1.5 hidden sm:inline text-sm">Mis pedidos</span>
              </Link>
            </Button>
            <Button
              onClick={() => setOpen(true)}
              variant="outline"
              className="relative border-cm-ink hover:bg-cm-ink hover:text-cm-cream"
              data-testid="open-cart-btn"
            >
              <ShoppingCart className="h-4 w-4" />
              <span className="ml-2 hidden sm:inline">Carrito</span>
              {count > 0 && (
                <span className="absolute -top-1.5 -right-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-cm-red px-1 text-[11px] font-bold text-white">
                  {count}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn" title="Salir">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {customer && (
          <div className="border-t border-cm-border/60 py-2 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1 text-xs" data-testid="customer-strip">
            {customer.nombre_representante && (
              <div className="flex items-center gap-1.5 text-cm-ink-soft truncate">
                <span className="uppercase tracking-widest text-[10px] font-medium">Cliente:</span>
                <span className="text-cm-ink font-medium truncate">{customer.nombre_representante}</span>
              </div>
            )}
            {customer.almacen && (
              <div className="flex items-center gap-1.5 text-cm-ink-soft truncate">
                <Store className="h-3 w-3 flex-shrink-0" />
                <span className="text-cm-ink truncate">{customer.almacen}</span>
              </div>
            )}
            {customer.direccion && (
              <div className="flex items-center gap-1.5 text-cm-ink-soft truncate">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{customer.direccion}{customer.ciudad ? `, ${customer.ciudad}` : ""}</span>
              </div>
            )}
            {customer.plazo_dias > 0 && (
              <div className="flex items-center gap-1.5 text-cm-ink-soft truncate">
                <span className="uppercase tracking-widest text-[10px] font-medium">Plazo:</span>
                <span className="text-cm-ink font-mono">{customer.plazo_dias} días</span>
                {customer.cupo > 0 && (
                  <span className="ml-1">· Cupo: <span className="font-mono text-cm-ink">{formatCOP(customer.cupo)}</span></span>
                )}
              </div>
            )}
          </div>
        )}

        {customer && bodegaStatus && !bodegaStatus.open && (
          <div
            className="py-2 px-3 mb-2 bg-amber-50 border border-amber-300 rounded text-xs flex items-center gap-2"
            data-testid="bodega-closed-banner"
          >
            <Clock className="h-3.5 w-3.5 text-amber-700 flex-shrink-0" />
            <span className="text-amber-900">
              <strong>Estamos cerrados ahora.</strong> Puedes seguir armando tu pedido — lo despacharemos cuando reabramos. {bodegaStatus.reopen_label}.
            </span>
          </div>
        )}

        {customer && cartera && cartera.status === "rojo" && (
          <div className="py-1.5 px-3 mb-2 bg-red-50 border border-red-300 rounded text-xs flex items-center gap-2" data-testid="cartera-alert-red">
            <AlertTriangle className="h-3.5 w-3.5 text-red-700 flex-shrink-0" />
            <span className="text-red-900">
              <strong>{cartera.n_vencidas} factura{cartera.n_vencidas === 1 ? "" : "s"} vencida{cartera.n_vencidas === 1 ? "" : "s"}</strong>
              {" "}({formatCOP(cartera.saldo_vencido)}) · Solo puedes pagar de contado.
              <Link to="/mi-cartera" className="ml-2 underline hover:text-red-950" data-testid="cartera-alert-detail">Ver detalle</Link>
              <a
                href={whatsappUrl("573045729333", `Hola, soy ${customer.razon_social || customer.almacen || "cliente"} (NIT ${customer.nit}) y quiero consultar mi estado de cartera.`)}
                onClick={(e) => {
                  e.preventDefault();
                  openWhatsApp("573045729333", `Hola, soy ${customer.razon_social || customer.almacen || "cliente"} (NIT ${customer.nit}) y quiero consultar mi estado de cartera.`);
                }}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono ml-2 inline-flex items-center gap-1 underline hover:text-red-950"
                data-testid="cartera-alert-whatsapp"
              >
                <MessageCircle className="h-3 w-3" /> Cartera
              </a>
            </span>
          </div>
        )}
        {customer && cartera && cartera.status === "amarillo" && (
          <div className="py-1.5 px-3 mb-2 bg-cm-yellow/30 border border-cm-yellow rounded text-xs flex items-center gap-2" data-testid="cartera-alert-yellow">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-800 flex-shrink-0" />
            <span className="text-amber-900">
              {cartera.mensaje}
              <Link to="/mi-cartera" className="ml-2 underline hover:text-amber-950" data-testid="cartera-alert-detail">Ver detalle</Link>
            </span>
          </div>
        )}
        {customer && usingGenericPwd && (
          <div className="py-1.5 px-3 mb-2 bg-cm-ink/5 border border-cm-border rounded text-xs flex items-center justify-between gap-2" data-testid="generic-password-banner">
            <span className="flex items-center gap-2 text-cm-ink-soft">
              <KeyRound className="h-3.5 w-3.5 flex-shrink-0" />
              <span>
                Estás usando la <strong>contraseña genérica</strong>. Te sugerimos crear una propia para mayor seguridad.
              </span>
            </span>
            <button
              type="button"
              onClick={() => setPwdOpen(true)}
              className="text-cm-red font-medium underline hover:text-cm-ink whitespace-nowrap"
              data-testid="set-personal-password-btn"
            >
              Crear contraseña
            </button>
          </div>
        )}
      </div>
      {customer && (
        <ChangePasswordModal
          open={pwdOpen}
          onOpenChange={setPwdOpen}
          isFirstTime={usingGenericPwd}
          blocking={mustChangePassword}
          onSuccess={markPasswordPersonal}
        />
      )}
    </header>
  );
};
